import React from 'react'
import { Store } from 'redux'
import { DispatchProp, connect } from 'react-redux'
import { Loader } from '@carsys/ui'

interface IState<TResult> {
  isLoading: boolean
  loadError?: any
  result?: TResult
}

export interface IWithDataProps<TResult> {
  result?: TResult
  setResult: (arg: TResult) => void
  reload: () => Promise<void>
}

/**
 * A higher order component that prevents the rendering of the sub components
 * until the supplied function's promise is resolved.
 *
 * @param {function: Promise} dataLoader An async function or a function that returns a promise.
 * @param {function: Promise} shouldUpdate A function which hooks into componentDidUpdate. when true, triggers a reload of the date.
 */
const withData =
  <TProps, TResult>(
    dataLoader: (props: TProps, store: Pick<Store<any>, 'dispatch'>) => Promise<TResult>,
    shouldUpdate?: (oldProps: any, newProps: any) => boolean
  ) =>
  (WrappedComponent: React.ComponentType<IWithDataProps<TResult>>) => {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component'

    class WithData extends React.Component<TProps & DispatchProp, IState<TResult>> {
      static displayName = `WithData(${displayName})`
      static WrappedComponent = WrappedComponent

      state: IState<TResult> = {
        isLoading: true,
        loadError: null,
        result: undefined
      }

      private _isMounted = false

      async loadData(passedProps: TProps) {
        try {
          const result = await dataLoader(passedProps, { dispatch: this.props.dispatch })
          if (this._isMounted) {
            this.setState({
              isLoading: false,
              loadError: null,
              result
            })
          }
        } catch (e: any) {
          console.error(e, e?.response)
          if (this._isMounted) {
            this.setState({ isLoading: false, loadError: e?.response?.data?.Error })
          }
        }
      }

      async componentDidMount() {
        this._isMounted = true
        await this.loadData(this.props)
      }

      async componentDidUpdate(prevProps: TProps) {
        if (shouldUpdate && shouldUpdate(prevProps, this.props)) {
          if (this._isMounted) this.setState({ isLoading: true })

          await this.loadData(this.props)
        }
      }

      async componentWillUnmount() {
        this._isMounted = false
      }

      reloadData = async () => {
        await this.loadData(this.props)
      }

      setResult = (data: any) => {
        this.setState({ result: data })
      }

      render() {
        const { isLoading, result, loadError } = this.state
        return isLoading ? (
          <Loader loading />
        ) : (
          <WrappedComponent {...this.props} result={result} loadError={loadError} setResult={this.setResult} reload={this.reloadData} />
        )
      }
    }

    return connect()<any>(WithData)
  }

export default withData
